export type TenantKind = "default" | "personal" | "team";

export interface Tenant {
  id: string;
  name: string;
  kind?: TenantKind;
  stepUpRequired?: boolean;
}

export interface User {
  id: string;
  email: string;
  name: string;
  displayName?: string;
  title?: string;
  employeeNo?: string;
  phone?: string;
  avatarUrl?: string;
  bio?: string;
  status: "active" | "suspended" | "deleted";
  createdAt: string;
  updatedAt: string;
}

export interface TokenPair {
  accessToken: string;
  accessExpiresAt: string;
  refreshToken: string;
  refreshExpiresAt: string;
}

export interface SessionToken {
  accessToken: string;
  accessExpiresAt: string;
  refreshExpiresAt: string;
  /** @deprecated Browser sessions use the HttpOnly refresh cookie. */
  refreshToken?: string;
}

export interface Session {
  token: SessionToken;
  user: User;
  tenant: Tenant;
  tenants: Tenant[];
  identifier: string;
}

export type TenantTicketCategory = "billing" | "quota" | "onboarding" | "other";

export type TenantTicketStatus = "pending" | "approved" | "rejected" | "closed";

export type TenantTicketRequestType =
  "general" | "plan_change" | "drive_storage" | "tenant_create";

export interface TenantTicket {
  body: string;
  category: TenantTicketCategory;
  createdAt: string;
  id: string;
  appliedDriveQuotaBytes?: number;
  appliedPlanCode?: string;
  appliedSeats?: number;
  appliedTenantId?: string;
  requestType: TenantTicketRequestType;
  requestedDriveQuotaBytes?: number;
  requestedPlanCode?: string;
  requestedPlanName?: string;
  requestedSeats?: number;
  requestedTenantName?: string;
  reviewedAt?: string;
  reviewNote?: string;
  status: TenantTicketStatus;
  subject: string;
  tenantId: string;
  updatedAt: string;
}

export interface CreateTenantTicketPayload {
  body: string;
  category: TenantTicketCategory;
  requestType?: TenantTicketRequestType;
  requestedDriveQuotaBytes?: number;
  requestedPlanCode?: string;
  requestedSeats?: number;
  requestedTenantName?: string;
  subject: string;
}

export interface TenantPlanCatalog {
  aiRequestsPerMinute: number;
  apiRequestsPerMinute: number;
  availableForRequest: boolean;
  basePriceMicros: number;
  code: string;
  currency: string;
  defaultSeats: number;
  description?: string;
  faasInvocationsPerDay: number;
  features: string[];
  maxSeats: number;
  name: string;
  pricingMode: "package" | "metered";
  settlementMode: "prepaid" | "postpaid";
  sortOrder: number;
  storageBytes: number;
}

export interface TenantPlanView {
  aiRequestsPerMinute: number;
  apiRequestsPerMinute: number;
  basePriceMicros: number;
  code: string;
  currency: string;
  effectiveAt: string;
  expiresAt?: string;
  faasInvocationsPerDay: number;
  features: string[];
  maxSeats: number;
  name: string;
  pricingMode: "package" | "metered";
  seats: number;
  settlementMode: "prepaid" | "postpaid";
  sortOrder: number;
  status: "active" | "suspended" | "expired";
  storageBytes: number;
}

export interface TenantBillingOverview {
  current: TenantPlanView;
  plans: TenantPlanCatalog[];
  ticketPolicy: TenantTicketPolicy;
}

export interface TenantUsageMetric {
  amountMicros: number;
  amountMicrosExact?: string;
  available: boolean;
  currency: string;
  hardLimit: number;
  hardLimitExact?: string;
  includedQuantity: number;
  includedQuantityExact?: string;
  key: string;
  name: string;
  overageQuantity: number;
  overageQuantityExact?: string;
  quantity: number;
  quantityExact?: string;
  resetPeriod: "none" | "minute" | "day" | "month";
  unit: "count" | "request" | "byte" | "token" | "millisecond" | "second";
  unitPriceMicros: number;
  unitPriceMicrosExact?: string;
  unitSize: number;
  unitSizeExact?: string;
}

export interface TenantUsageSummary {
  amountMicros: number;
  amountMicrosExact?: string;
  balanceMicros: number;
  balanceMicrosExact?: string;
  basePriceMicros: number;
  basePriceMicrosExact?: string;
  currency: string;
  estimatedRemainingBalanceMicros: number;
  estimatedRemainingBalanceMicrosExact?: string;
  generatedAt: string;
  meteredAmountMicros: number;
  meteredAmountMicrosExact?: string;
  metrics: TenantUsageMetric[];
  periodEnd: string;
  periodStart: string;
  pricingMode: "package" | "metered";
  settlementMode: "prepaid" | "postpaid";
  tenantId: string;
}

export interface TenantTicketPolicy {
  allowTicketCreation: boolean;
  reason?: string;
}

export type ApprovalScope = "peer" | "tenant" | "platform";
export type ApprovalStatus =
  | "pending"
  | "approved"
  | "executing"
  | "succeeded"
  | "failed"
  | "rejected"
  | "cancelled"
  | "expired";
export type ApprovalDecisionValue = "approve" | "reject";
export type ApprovalView = "submitted" | "pending" | "decided";

export interface ApprovalFormOption {
  label: string;
  value: string;
}

export interface ApprovalFormField {
  control: "date" | "integer" | "number" | "select" | "text" | "textarea";
  defaultValue?: boolean | number | string;
  key: string;
  label: string;
  maximum?: number;
  maxLength?: number;
  minimum?: number;
  options?: ApprovalFormOption[];
  placeholder?: string;
  required?: boolean;
}

export interface ApprovalSubmissionTemplate {
  approverRule: "tenant_admin";
  category: string;
  description?: string;
  fields: ApprovalFormField[];
  key: string;
  scopeKind: ApprovalScope;
  title: string;
  version: number;
}

export interface SubmitApprovalInput {
  idempotencyKey: string;
  payload: Record<string, unknown>;
  reason: string;
  templateKey: string;
}

export interface ApprovalActor {
  subjectType: "user" | "application" | "system";
  subjectId: string;
  tenantId?: string;
  userId?: string;
  displayName?: string;
}

export interface ApprovalTask {
  id: string;
  instanceId: string;
  stepNo: number;
  mode: "any" | "all" | "quorum";
  quorum: number;
  requireStepUp: boolean;
  assignee: ApprovalActor;
  status: "waiting" | "pending" | "approved" | "rejected" | "cancelled";
  decidedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApprovalDecision {
  id: string;
  instanceId: string;
  taskId: string;
  actor: ApprovalActor;
  decision: ApprovalDecisionValue;
  comment?: string;
  createdAt: string;
}

export interface ApprovalInstance {
  id: string;
  templateKey: string;
  templateVersion: number;
  sourceService: string;
  sourceKey: string;
  executionService: string;
  scopeKind: ApprovalScope;
  tenantId?: string;
  requester: ApprovalActor;
  targetType: string;
  targetId: string;
  title: string;
  reason: string;
  payload?: Record<string, unknown>;
  status: ApprovalStatus;
  currentStep: number;
  executionRequired: boolean;
  executionGeneration: number;
  executionError?: string;
  executionResult?: Record<string, unknown>;
  expiresAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  tasks?: ApprovalTask[];
  decisions?: ApprovalDecision[];
}

export interface DriveLimitSummary {
  maxTeamSpaces: number;
  teamSpacesCreated: number;
  tenantQuotaBytes: number;
  tenantReservedBytes: number;
  tenantUsedBytes: number;
}

export interface DriveLimitRequest {
  id: string;
  kind: "tenant_quota" | "space_quota" | "team_space_count";
  reason: string;
  requestedAt: string;
  requestedValue: number;
  requesterUserId: string;
  reviewedAt?: string;
  reviewNote?: string;
  status: "pending" | "approved" | "rejected";
}

export type FaaSRuntime =
  "builtin" | "http" | "deno" | "llrt" | "bun" | "container" | "mcp" | "skill";

export type FaaSFunctionStatus = "enabled" | "disabled";
export type FaaSVersionStatus = "ready" | "retired" | "failed";
export type FaaSInvocationStatus =
  | "queued"
  | "running"
  | "succeeded"
  | "failed"
  | "timed_out"
  | "cancel_requested"
  | "cancelled";
export type FaaSFunctionAccessTokenStatus = "active" | "revoked";

export interface FaaSToolDefinition {
  annotations?: Record<string, boolean>;
  description: string;
  executionPolicy?: {
    agent?: "auto" | "confirm";
  };
  inputSchema: Record<string, unknown>;
  name: string;
  outputSchema?: Record<string, unknown>;
}

export interface FaaSGrantableTool {
  allowedSubjectTypes: Array<"application" | "user">;
  annotations: Record<string, boolean>;
  bundleSlug?: string;
  description: string;
  executionPolicy?: "auto" | "confirm";
  functionId: string;
  name: string;
  requestedCapabilities: string[];
  source: "bundle" | "direct";
  versionId: string;
}

export interface FaaSOAuthClientToolPolicy {
  clientId: string;
  functionIds: string[];
  generation: number;
  mode: "explicit" | "legacy_all" | "none";
  updatedAt?: string;
  updatedBy?: string;
}

export interface ReplaceFaaSOAuthClientToolGrantsPayload {
  expectedGeneration: number;
  functionIds: string[];
}

interface FaaSManifestBase {
  apiVersion: "ark.platform/v1alpha1";
  kind: "Function";
  metadata: {
    name: string;
    version: string;
  };
}

interface FaaSManifestSpecBase {
  authorization?: {
    allowedSubjectTypes: Array<"application" | "user">;
  };
  capabilities: string[];
  limits: {
    maxInputBytes: number;
    maxOutputBytes: number;
    timeoutMs: number;
  };
  retry: {
    maxAttempts: number;
  };
  trust: "external_connector" | "trusted_inner";
}

type FaaSManifestRuntime = {
  artifactDigest?: string;
  artifactUri?: string;
  bindings?: {
    credential?: {
      installationParameter: string;
    };
    parameters?: Record<
      string,
      {
        installationParameter: string;
      }
    >;
  };
  endpoint?: string;
  entrypoint?: string;
  source?: string;
  sourceRevision?: string;
};

export type FaaSToolManifest = FaaSManifestBase & {
  spec: FaaSManifestSpecBase & {
    runtime: FaaSManifestRuntime & { kind: Exclude<FaaSRuntime, "skill"> };
    skill?: never;
    tool: FaaSToolDefinition;
  };
};

export type FaaSSkillManifest = FaaSManifestBase & {
  spec: FaaSManifestSpecBase & {
    runtime: FaaSManifestRuntime & { entrypoint: "SKILL.md"; kind: "skill" };
    skill: {
      description: string;
      requiredBundles?: Array<{ slug: string; version: string }>;
      requiredTools?: string[];
      title: string;
    };
    tool?: never;
  };
};

export type FaaSManifest = FaaSSkillManifest | FaaSToolManifest;

export interface FaaSFunction {
  activeVersionId?: string;
  createdAt: string;
  createdBy: string;
  description: string;
  distributionMode: "bundle" | "direct";
  displayName: string;
  externalAccessEnabled: boolean;
  id: string;
  slug: string;
  status: FaaSFunctionStatus;
  tenantId: string;
  updatedAt: string;
  visibility: "tenant" | "global";
}

export type FaaSBundleStatus = "active" | "deprecated";
export type FaaSBundleVersionStatus =
  "draft" | "reviewing" | "published" | "rejected" | "deprecated" | "revoked";
export type FaaSBundleInstallationStatus =
  "active" | "disabled" | "uninstalled";
export type FaaSInstallationConfigurationStatus =
  "invalid" | "not_required" | "pending" | "ready";
export type FaaSInstallationParameterKind =
  "boolean" | "integer" | "secret" | "string";

export interface FaaSBundleMember {
  displayOrder: number;
  functionId: string;
  functionVersionId: string;
  kind: "skill" | "tool";
  skillRef?: string;
  toolName: string;
}

export interface FaaSBundleSkillDetail {
  content: string;
  contentDigest: string;
  package?: SkillContentPackage;
  packageDigest?: string;
  description: string;
  entrypoint: "SKILL.md";
  requiredBundles: Array<{ slug: string; version: string }>;
  requiredTools: string[];
  title: string;
}

export interface SkillDraftFile {
  path: string;
  executable: boolean | null;
  /** Base64 of the original bytes, including binary attachments. */
  content: string;
}

export interface SkillContentPackage {
  schemaVersion: number;
  id: string;
  version: string;
  entrypoint: string;
  files: Array<SkillDraftFile & { executable: boolean }>;
}

export interface SkillDraftMetadata {
  displayName: string;
  description: string;
  tags: string[];
  requiredBundles: Array<{ slug: string; version: string }>;
  requiredTools: string[];
}

export interface SkillDraft {
  id: string;
  revision: number;
  entrypoint: string;
  metadata: SkillDraftMetadata;
  files: SkillDraftFile[];
  createdAt: string;
  updatedAt: string;
}

export interface SkillDraftSummary {
  id: string;
  revision: number;
  entrypoint: string;
  metadata: SkillDraftMetadata;
  fileCount: number;
  updatedAt: string;
  bundleId?: string;
  lastVersion?: string;
  lastRevision?: number;
}

export interface SaveSkillDraftPayload {
  operationId: string;
  expectedRevision: number;
  entrypoint: string;
  metadata: SkillDraftMetadata;
  files: SkillDraftFile[];
}

export interface FreezeSkillDraftPayload {
  operationId: string;
  expectedRevision: number;
  version: string;
}

export interface SkillDraftRelease {
  skillId: string;
  version: string;
  packageDigest: string;
  bundleId: string;
  bundleVersionId: string;
  functionVersionId: string;
  source: string;
}

export interface SkillDraftReceipt {
  operationId: string;
  draftId: string;
  revision: number;
  action: "save" | "freeze";
  release?: SkillDraftRelease;
  completedAt: string;
}

export interface SkillPin {
  id: string;
  source: string;
  version: string;
  digest: string;
}

export type SkillGroupRef = SkillPin;

export type SkillGroupReceipt = Omit<SkillDraftReceipt, "action"> & {
  action: "group-save" | "group-freeze";
  group?: SkillGroupRef;
};

export interface SkillGroupMember {
  skill: SkillPin;
  requires?: Array<{ skill: SkillPin; condition?: string; optional?: boolean }>;
  contexts?: Array<{ path: string; condition?: string }>;
  runtimes?: Array<{
    capability: string;
    condition?: string;
    optional?: boolean;
  }>;
}

export interface SkillGroupDraft {
  id: string;
  revision: number;
  name: string;
  description: string;
  members: SkillGroupMember[];
  updatedAt?: string;
}

export interface SkillGroupVersion {
  ref: SkillGroupRef;
  manifest: {
    schemaVersion: 2;
    id: string;
    version: string;
    name: string;
    description: string;
    members: SkillGroupMember[];
  };
  createdAt: string;
}

export interface SkillContextBinding {
  path: string;
  mode: "live" | "snapshot";
  required?: boolean;
  initial?: boolean;
  digest?: string;
  condition?: string;
}

export interface SkillWorkspaceProfile {
  schemaVersion: 2;
  id: string;
  groups?: SkillGroupRef[];
  members?: SkillGroupMember[];
  contexts?: SkillContextBinding[];
  conditions?: string[];
}

export interface SkillResolvedProfile {
  schemaVersion: 2;
  profileId: string;
  groups: SkillGroupRef[];
  members: SkillGroupMember[];
  contexts: Array<SkillContextBinding & { observedDigest?: string }>;
  conditions: string[];
  digest: string;
}

export interface SkillProfilePreview {
  resolution: SkillResolvedProfile;
  issues: Array<{
    code: string;
    subject: string;
    detail: string;
    blocking: boolean;
  }>;
  ready: boolean;
}

export interface SkillContextSnapshot {
  path: string;
  content: string;
  digest: string;
}

export interface SkillCloudSelection {
  profile: SkillWorkspaceProfile;
  snapshots: SkillContextSnapshot[];
}

export interface SkillCloudProfile {
  selection?: SkillCloudSelection | null;
  generation: number;
  updatedAt?: string;
}

export interface SkillArchiveInspection {
  files: SkillDraftFile[];
  entrypoints: string[];
  rootPrefix: string;
}

export interface SkillFileSuggestion {
  action: "create" | "update" | "delete" | "rename";
  path: string;
  newPath?: string;
  content?: string;
  baseDigest?: string;
}

export interface SkillAuthoringSuggestion {
  draftId: string;
  revision: number;
  metadata?: Pick<SkillDraftMetadata, "displayName" | "description" | "tags">;
  changes: SkillFileSuggestion[];
}

/** Durable Skill authoring receipt; the first committed result is replayed. */
export interface SkillAIReceipt {
  requestId: string;
  operation: "suggest" | "analyze";
  runId: string;
  status: "pending" | "committed" | "failed" | "cancelled";
  runStatus?: string;
  errorCode?: string;
  errorMessage?: string;
  suggestion?: SkillAuthoringSuggestion;
  analysis?: { content: string };
  createdAt: string;
  completedAt?: string;
}

export interface FaaSBundleMemberDetail {
  allowedSubjectTypes: Array<"application" | "user">;
  entrypoint?: string;
  functionDescription: string;
  functionDisplayName: string;
  functionId: string;
  functionSlug: string;
  functionVersion: string;
  functionVersionId: string;
  kind: "skill" | "tool";
  manifest: FaaSManifest;
  manifestDigest: string;
  maxAttempts: number;
  maxInputBytes: number;
  maxOutputBytes: number;
  requestedCapabilities: string[];
  runtimeKind: FaaSRuntime;
  source?: string;
  skill?: FaaSBundleSkillDetail;
  skillRef?: string;
  timeoutMs: number;
  tool: FaaSToolDefinition;
  toolName: string;
}

export interface FaaSBundleImportResult {
  bundle: FaaSBundle;
  created: boolean;
  version: FaaSBundleVersion;
  versionCreated: boolean;
}

export interface FaaSBundleVersion {
  approvalGeneration: number;
  approvalId?: string;
  bundleId: string;
  createdAt: string;
  createdBy: string;
  id: string;
  installationParameters?: Record<string, FaaSInstallationParameter>;
  members: FaaSBundleMember[];
  publishedAt?: string;
  reviewReason?: string;
  reviewedBy?: string;
  revokedAt?: string;
  status: FaaSBundleVersionStatus;
  submittedAt?: string;
  version: string;
}

export interface FaaSInstallationParameter {
  defaultValue?: unknown;
  description?: string;
  displayName: string;
  kind: FaaSInstallationParameterKind;
  overridePolicy: "platform_only" | "tenant_optional" | "tenant_required";
  required: boolean;
  validation?: Record<string, unknown>;
}

export interface FaaSBundle {
  category?: string;
  createdAt: string;
  createdBy: string;
  description: string;
  displayName: string;
  icon?: string;
  id: string;
  latestVersion?: string;
  latestVersionId?: string;
  listing: "listed" | "unlisted";
  visibility?: "tenant" | "global";
  publicationGeneration?: number;
  ownerTenantId: string;
  slug: string;
  status: FaaSBundleStatus;
  tags: string[];
  toolCount: number;
  updatedAt: string;
}

export interface FaaSBundleDetail extends FaaSBundle {
  versions: FaaSBundleVersion[];
}

export interface FaaSBundleInstallation {
  activeConfigRevisionId?: string;
  bundleId: string;
  bundleName: string;
  bundleSlug: string;
  bundleVersion: string;
  bundleVersionId: string;
  configGeneration: number;
  configurationStatus: FaaSInstallationConfigurationStatus;
  generation: number;
  id: string;
  installedAt: string;
  installedBy: string;
  status: FaaSBundleInstallationStatus;
  tenantId: string;
  updatedAt: string;
}

export type FaaSBundleInstallationSummary = Pick<
  FaaSBundleInstallation,
  | "id"
  | "bundleId"
  | "bundleName"
  | "bundleSlug"
  | "bundleVersionId"
  | "bundleVersion"
  | "status"
  | "configurationStatus"
> & { toolCount: number };

export type FaaSBundleInstallationRequestSummary = Pick<
  FaaSBundleInstallationRequest,
  | "id"
  | "bundleId"
  | "bundleName"
  | "bundleVersionId"
  | "bundleVersion"
  | "approvalId"
  | "installationId"
  | "createdAt"
>;

export interface FaaSInstallationParameterState {
  configured: boolean;
  defaultValue?: unknown;
  description?: string;
  displayName: string;
  effectiveSource: "default" | "missing" | "platform" | "tenant";
  key: string;
  kind: FaaSInstallationParameterKind;
  overridePolicy: "platform_only" | "tenant_optional" | "tenant_required";
  required: boolean;
  validation?: Record<string, unknown>;
  value?: unknown;
}

export interface FaaSInstallationConfiguration {
  bundleVersionId: string;
  generation: number;
  installationId: string;
  parameters: FaaSInstallationParameterState[];
  status: FaaSInstallationConfigurationStatus;
  updatedAt?: string;
}

export interface FaaSBundleInstallationRequest {
  approvalGeneration: number;
  approvalId?: string;
  bundleId: string;
  bundleName: string;
  bundleSlug: string;
  bundleVersion: string;
  bundleVersionId: string;
  createdAt: string;
  id: string;
  idempotencyKey: string;
  installationId?: string;
  reason: string;
  requestedBy: string;
  requesterDisplay?: string;
  submittedAt?: string;
  tenantId: string;
  updatedAt: string;
}

export interface FaaSBundleInstallationHistory {
  changeKind:
    "disable" | "enable" | "install" | "rollback" | "uninstall" | "upgrade";
  changedAt: string;
  changedBy: string;
  fromGeneration: number;
  fromVersionId?: string;
  id: string;
  installationId: string;
  reason?: string;
  toGeneration: number;
  toVersionId?: string;
}

export type SkillExposureMode = "off" | "selected_metadata" | "catalog_tools";

export interface SkillMarketItem {
  bundleDescription: string;
  bundleId: string;
  bundleName: string;
  bundleSlug: string;
  bundleVersion: string;
  bundleVersionId: string;
  installationId?: string;
  installationGeneration?: number;
  installationStatus?: FaaSBundleInstallationStatus;
  installed: boolean;
  installedBundleVersion?: string;
  installedBundleVersionId?: string;
  skillCount: number;
  toolCount?: number;
  ownerTenantId?: string;
  createdBy?: string;
  listing?: "listed" | "unlisted";
  visibility?: "tenant" | "global";
  publicationGeneration?: number;
  configurationStatus?: FaaSInstallationConfigurationStatus;
  members?: SkillMarketMember[];
  installedMembers?: FaaSBundleMember[];
}

export interface SkillMarketMember {
  functionId: string;
  functionVersionId: string;
  name: string;
  skillRef: string;
  version: string;
  title: string;
  description: string;
  source: string;
}

export interface SkillPublicationVersion {
  bundleId: string;
  bundleVersionId: string;
  version: string;
  subjectDigest: string;
  attested: boolean;
}

export interface SkillPublicationAttestation {
  bundleVersionId: string;
  subjectDigest: string;
  sourceUri: string;
  sourceRevision: string;
  builderId: string;
  scanDigest: string;
  scanStatus: "passed";
  signingKeyId: string;
  publicKey: string;
  signature: string;
}

export interface PublishSkillBundlePayload {
  bundleVersionId: string;
  visibility: "private" | "tenant" | "global";
  expectedGeneration: number;
  reason: string;
  attestations?: SkillPublicationAttestation[];
}

export interface SkillSummary {
  bundleId?: string;
  bundleVersionId?: string;
  contentDigest?: string;
  description: string;
  enabled: boolean;
  functionId?: string;
  functionVersionId?: string;
  installationId?: string;
  selected: boolean;
  skillKey: string;
  skillRef: string;
  title: string;
}

export interface SkillContextPlan {
  exposureCeiling?: SkillExposureMode;
  exposureMode: SkillExposureMode;
  expiresAt?: string;
  runKey?: string;
  requiredTools: string[];
  skills: SkillSummary[];
  surface: string;
}

export interface SkillTenantPolicy {
  allowedSkillRefs: string[];
  enabledByDefault: boolean;
  exposureMode: SkillExposureMode;
  generation: number;
  surface: string;
  tenantId: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface UserSkillPreferences {
  disabledSkillRefs: string[];
  enabledSkillRefs: string[];
  exposureMode: SkillExposureMode;
  generation: number;
  selectedSkillRefs: string[];
  surface: string;
  tenantId: string;
  updatedAt?: string;
  userId: string;
}

export interface FaaSVersion {
  artifactDigest?: string;
  artifactUri?: string;
  createdAt: string;
  createdBy: string;
  endpoint?: string;
  entrypoint: string;
  functionId: string;
  id: string;
  manifest: FaaSManifest;
  manifestDigest: string;
  maxInputBytes: number;
  maxOutputBytes: number;
  requestedCapabilities: string[];
  runtimeKind: FaaSRuntime;
  status: FaaSVersionStatus;
  tenantId: string;
  timeoutMs: number;
  tool: FaaSToolDefinition;
  version: string;
}

export interface FaaSInvocation {
  attempt: number;
  callerAppId?: string;
  callerAppSlug?: string;
  callerId: string;
  callerType: string;
  callerUserId?: string;
  completedAt?: string;
  createdAt: string;
  deadline: string;
  durationMs: number;
  errorCode?: string;
  errorMessage?: string;
  functionId: string;
  functionSlug: string;
  generation: number;
  id: string;
  idempotencyKey: string;
  input: unknown;
  leaseExpiresAt?: string;
  leaseOwner?: string;
  output?: unknown;
  requestBytes: number;
  responseBytes: number;
  startedAt?: string;
  status: FaaSInvocationStatus;
  tenantId: string;
  traceId?: string;
  updatedAt: string;
  version: FaaSVersion;
  versionId: string;
}

export interface FaaSFunctionAccessToken {
  callerTenantId: string;
  capabilities: string[];
  createdAt: string;
  createdBy: string;
  expiresAt?: string;
  functionId: string;
  id: string;
  lastUsedAt?: string;
  name: string;
  revokedAt?: string;
  revokedBy?: string;
  status: FaaSFunctionAccessTokenStatus;
  tokenPrefix: string;
  updatedAt: string;
}

export interface IssuedFaaSFunctionAccessToken {
  faas_token: string;
  item: FaaSFunctionAccessToken;
}

export interface FaaSDenoVersionSource {
  artifactDigest: string;
  entrypoint: "mod.ts";
  functionId: string;
  source: string;
  versionId: string;
}

export interface CreateFaaSFunctionPayload {
  description?: string;
  displayName: string;
  externalAccessEnabled: boolean;
  slug: string;
}

export interface PublishFaaSDenoSourcePayload {
  manifest: FaaSManifest;
  source: string;
}

export interface CreateFaaSFunctionAccessTokenPayload {
  capabilities: string[];
  expiresAt?: string;
  name: string;
}

export interface InvokeFaaSFunctionPayload {
  idempotencyKey?: string;
  input: Record<string, unknown>;
  timeoutMs?: number;
  waitMs?: number;
}

// Permissions 是当前登录用户的权限 scope 列表，从独立接口 GET /user/me/permissions
// 拉取，刻意不糅进 Session/User，避免在只需资料的地方回传权限信息。
export interface Permissions {
  scopes: string[];
}

// hasScope 逐层前缀通配匹配，语义与后端 auth.Principal.HasScope 一致：
// 命中 "*"、精确值、或任一层前缀的 "<prefix>:*" 通配即为 true。
// 例：scopes 含 "apps:*" 时，hasScope(scopes, "apps:drive:*") 为 true。
export function hasScope(scopes: string[], target: string): boolean {
  if (scopes.includes("*") || scopes.includes(target)) return true;
  for (let i = 0; i < target.length; i++) {
    if (target[i] === ":" && scopes.includes(`${target.slice(0, i)}:*`)) {
      return true;
    }
  }
  return false;
}

export interface PreAuthChallenge {
  identifier: string;
  preAuthToken: string;
  expiresAt: string;
  tenants: Tenant[];
}

export interface ApiRequest {
  auth?: boolean;
  body?: unknown;
  headers?: HeadersInit;
  signal?: AbortSignal;
  onError?: (
    error: unknown,
    config: { method: string; path: string },
  ) => Error | void;
}

export interface ApiClient {
  delete<T>(path: string, request?: ApiRequest): Promise<T>;
  get<T>(path: string, request?: Omit<ApiRequest, "body">): Promise<T>;
  patch<T>(path: string, request?: ApiRequest): Promise<T>;
  post<T>(path: string, request?: ApiRequest): Promise<T>;
  postStream?(path: string, request?: ApiRequest): Promise<Response>;
  put<T>(path: string, request?: ApiRequest): Promise<T>;
  resolve(path: string): string;
}

export interface AppRuntime {
  api: ApiClient;
  displayMode?: "embedded" | "standalone" | "picture-in-picture";
  getSession(): Session | null;
  navigate(to: string): void;
  // Long-running tasks can ask the shell to keep their remote effects active
  // after navigation. Callers must aggregate concurrent tasks and clear the
  // flag when the final task completes or is cancelled.
  setBackgroundActivity?: (active: boolean) => void;
  subscribeSession(listener: () => void): () => void;
  notifications?: NotificationRuntime;
}

export type NotificationVisibility = "global" | "channel_only" | "both";
export type NotificationImportance = "low" | "normal" | "high" | "critical";
export type NotificationMedium = "in_app" | "email" | "sms" | "app_push";

export interface NotificationMessage {
  id: string;
  tenantId: string;
  type: string;
  channel: string;
  visibility: NotificationVisibility;
  importance: NotificationImportance;
  title: string;
  body: string;
  data: Record<string, unknown>;
  actionUrl?: string;
  sourceType: "system" | "application";
  sourceId: string;
  readAt?: string;
  createdAt: string;
}

export interface NotificationPage {
  items: NotificationMessage[];
  total: number;
}

export interface NotificationChangedEvent {
  type: "notification.created";
  // transient 事件不落库，因此没有 notificationId；用 dedupeKey 做客户端去重。
  notificationId?: string;
  channel: string;
  visibility: NotificationVisibility;
  importance: NotificationImportance;
  title?: string;
  body?: string;
  actionUrl?: string;
  data?: Record<string, unknown>;
  transient?: boolean;
  dedupeKey?: string;
}

export interface NotificationListOptions {
  unreadOnly?: boolean;
  page?: number;
  pageSize?: number;
}

// NotificationRuntime is bound by the shell. A remote application receives its
// own app.<slug> channel and cannot select another channel through this API.
export interface NotificationRuntime {
  readonly channel?: string;
  list(options?: NotificationListOptions): Promise<NotificationPage>;
  unreadCount(): Promise<number>;
  markRead(notificationId: string): Promise<void>;
  markAllRead(): Promise<number>;
  subscribe(listener: (event: NotificationChangedEvent) => void): () => void;
  listPreferences(): Promise<NotificationPreference[]>;
  updatePreference(
    preference: Omit<NotificationPreference, "updatedAt">,
  ): Promise<NotificationPreference>;
  registerPushDevice(
    device: NotificationPushDeviceRegistration,
  ): Promise<NotificationPushDevice>;
  unregisterPushDevice(deviceId: string): Promise<void>;
}

export interface NotificationPreference {
  channel: string;
  medium: NotificationMedium;
  enabled: boolean;
  minImportance: NotificationImportance;
  updatedAt?: string;
}

export interface NotificationPushDeviceRegistration {
  provider: "apns" | "fcm" | "webpush";
  platform: "ios" | "android" | "web";
  token: string;
}

export interface NotificationPushDevice {
  id: string;
  provider: NotificationPushDeviceRegistration["provider"];
  platform: NotificationPushDeviceRegistration["platform"];
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FederatedAppProps {
  runtime: AppRuntime;
}

export interface ExternalAppProps extends FederatedAppProps {
  path: string;
}

export interface DriveSpaceUsage {
  id: string;
  name: string;
  kind: "personal" | "team" | "app";
  usedBytes: number;
  quotaBytes: number;
}

export interface DriveDashboardSummary {
  spaceCount: number;
  usedBytes: number;
  quotaBytes: number;
  activeExternalLinks: number;
  externalAccessCount: number;
  externalDownloadCount: number;
  spaces: DriveSpaceUsage[];
}

export interface ImDashboardSummary {
  conversationCount: number;
  activeConversationCount: number;
  totalUnreadCount: number | null;
  unreadConversationCount: number | null;
  unreadCountCapped: boolean | null;
  readState: "fresh" | "partial" | "unavailable";
}

export const IM_UNREAD_CHANGED_EVENT = "ark:im-unread-changed";

// ---------- contact / friends (C 端好友) ----------
// 手写镜像后端 biz/contact 的值对象（见 services/iam/internal/biz/contact/vo.go）。
// avatarUrl 是版本化头像对象的稳定公有读 URL；时间字段为 ISO-8601 字符串。

// Friend 是好友列表的一项。friendshipId 用于删除好友。
export interface Friend {
  friendshipId: string;
  identityId: string;
  displayName: string;
  avatarUrl: string;
  bio: string;
  since: string;
}

// FriendRequest 是一条待处理请求。id 是关系行 id，用于 respond。
export interface FriendRequest {
  id: string;
  identityId: string;
  displayName: string;
  avatarUrl: string;
  bio: string;
  requestedAt: string;
}

// Page 是 keyset 分页结果：nextToken 为空表示到底。
export interface Page<T> {
  items: T[];
  nextToken: string;
}

export interface CursorPage<T> {
  items: T[];
  nextCursor: string;
  hasMore: boolean;
}

// OffsetPage 对齐后端 querykit.Page[T]，用于可随机翻页的管理/列表界面。
export interface OffsetPage<T> {
  items: T[];
  total: number;
}

// FriendRelation 是搜索候选与「我」的关系态，决定前端按钮显示。
export type FriendRelation =
  "none" | "friend" | "incoming" | "outgoing" | "blocked" | "self";

// SearchResult 是「按账号搜索添加好友」的一个候选（按 email 或手机号精确命中）。
export interface SearchResult {
  identityId: string;
  displayName: string;
  avatarUrl: string;
  bio: string;
  relation: FriendRelation;
}

// ---------- directory / departments (企业通讯录·组织架构) ----------
// 手写镜像后端 biz/directory 的值对象（见 services/iam/internal/biz/directory/vo.go）。

// Department 是组织架构树的一个节点。parentId 为空表示根部门。
export interface Department {
  id: string;
  parentId?: string;
  name: string;
  description?: string;
  path: string;
  sortOrder: number;
  memberCount: number;
}

// DirectoryMember 是通讯录成员：User 的公开资料 + 部门内岗位覆盖。
// deptTitle / isPrimary 仅在按部门列成员时有值，scope=all/unassigned 时为空。
export interface DirectoryMember extends User {
  activationEligible: boolean;
  deptTitle?: string;
  isPrimary?: boolean;
}

// ---------- registration / tenant (注册 + 创建/加入租户 + 审批) ----------
// 手写镜像后端 biz/user/auth 与 biz/tenant 的值对象。

// VerificationChallenge 是「发送验证码」返回的挑战句柄，注册时回传 verificationId。
export interface VerificationChallenge {
  verificationId: string;
  expiresAt: string;
}

export interface CaptchaPublicConfig {
  enabled: boolean;
  provider: string;
  captchaId: string;
}

// PasswordCodeChallenge 是登录态改密发码响应：接收手机号由后端按 identity 解析，
// 前端仅展示脱敏值，不能指定发送目标。
export interface PasswordCodeChallenge extends VerificationChallenge {
  maskedPhone: string;
}

// VerificationPurpose 是验证码用途（ADR-0005 D2）：发码与消费须一致，防跨流程重放。
export type VerificationPurpose =
  | "register"
  | "member_activation"
  | "reset_password"
  | "bind_phone"
  | "change_phone";

// RegisterPayload 是注册提交体（ADR-0005 D1：手机号为主标识并强制短信验证）。
export interface RegisterPayload {
  phone: string;
  phoneVerificationId: string;
  phoneCode: string;
  password: string;
  displayName?: string;
}

// ActivatePayload 是成员激活提交体（ADR-0005 D1：激活须绑定并验证手机）。
export interface ActivatePayload {
  token: string;
  password: string;
  phone: string;
  phoneVerificationId: string;
  phoneCode: string;
}

// ChangePasswordPayload 是登录密码短信改密提交体（ADR-0005 D3）。
export interface ChangePasswordPayload {
  verificationId: string;
  code: string;
  newPassword: string;
}

// ResetPasswordPayload 是「忘记密码」找回提交体（ADR-0005 D3）。
export interface ResetPasswordPayload {
  phone: string;
  verificationId: string;
  code: string;
  newPassword: string;
}

// ChangeTenantPasswordPayload 是租户 step-up 密码自助改密提交体（旧密码校验）。
export interface ChangeTenantPasswordPayload {
  oldPassword: string;
  newPassword: string;
}

// TenantSummary 是租户精简视图；inviteCode 仅当前租户与创建者视角回显。
export interface TenantSummary {
  id: string;
  name: string;
  kind: TenantKind;
  status: "active" | "suspended" | "deleted";
  inviteCode?: string;
}

export interface TenantAuthPolicy {
  sessionTtlDays: number;
  updatedByUserId?: string;
  updatedAt: string;
}

// JoinRequestStatus 是加入申请状态。
export type JoinRequestStatus = "pending" | "approved" | "rejected";

// JoinRequest 是一条加入申请（申请人视角 / 审批人列表项）。
export interface JoinRequest {
  id: string;
  tenantId: string;
  applicantDisplay: string;
  status: JoinRequestStatus;
  note?: string;
  requestedAt: string;
}

// ActivationInfo 是成员激活页据 token 展示的邀请上下文（镜像后端 bizauth.ActivationInfo）。
export interface ActivationInfo {
  email: string;
  displayName: string;
  tenantName: string;
  tenantKind: TenantKind;
  expiresAt: string;
}

// ---------- application / app center (应用中心) ----------
// 手写镜像后端 biz/application 的三层值对象（catalog / launch targets / installation）。

export type ApplicationTargetKind =
  "inner" | "iframe" | "external_link" | "native_link";
export type ApplicationPlatform = "web" | "ios" | "android" | "desktop";

// ApplicationStatus 应用状态。
export type ApplicationStatus = "active" | "disabled";

export interface ApplicationLaunchTarget {
  id: string;
  targetKey: string;
  platform: ApplicationPlatform;
  kind: ApplicationTargetKind;
  label?: string;
  remoteName?: string;
  remoteEntryUrl?: string;
  exposeModule?: string;
  mountPath?: string;
  url?: string;
  launchUri?: string;
  fallbackUrl?: string;
  iframeSandbox?: string;
  iframeAllow?: string;
  isPrimary: boolean;
  sortOrder: number;
}

export interface ApplicationCapability {
  code: string;
  name: string;
  riskLevel: "low" | "medium" | "high" | "critical";
  description?: string;
  consentRequired: boolean;
  required: boolean;
}

export interface ApplicationVersion {
  id: string;
  semver: string;
  trust: "sandboxed" | "trusted_inner";
  status: "published" | "deprecated" | "revoked";
  imageRef?: string;
  imageDigest?: string;
  launchTargets: ApplicationLaunchTarget[];
  capabilities: ApplicationCapability[];
  publishedAt?: string;
}

export type ApplicationLaunchPreferenceSource =
  "user" | "tenant" | "platform" | "manifest";

export interface ApplicationLaunchPreference {
  platformTargetKey?: string;
  tenantTargetKey?: string;
  userTargetKey?: string;
  effectiveTargetKey: string;
  effectiveSource: ApplicationLaunchPreferenceSource;
}

// Application 是当前租户的一条安装；打开方式是一对多 launchTargets。
export interface Application {
  id: string;
  catalogId: string;
  slug: string;
  name: string;
  description?: string;
  icon?: string;
  launchTargets: ApplicationLaunchTarget[];
  launchPreference?: ApplicationLaunchPreference;
  version: ApplicationVersion;
  grantedCapabilities: string[];
  autoUpdatePolicy: "manual" | "patch" | "minor";
  installedBy?: string;
  installedAt: string;
  // menuOrder 侧边栏 / 卡片排序权重。
  menuOrder: number;
  // requiredScope 可见性门槛：空表示登录即可见。
  requiredScope?: string;
  // autoGrant 为 true 时，本租户所有成员登录即自动获得 app:<slug>:* scope。
  autoGrant?: boolean;
  // isCore 为 true 时应用不可停用或删除。
  isCore: boolean;
  status: ApplicationStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CatalogApplication {
  id: string;
  slug: string;
  name: string;
  description?: string;
  icon?: string;
  isCore: boolean;
  defaultAutoGrant: boolean;
  defaultRequiredScope?: string;
  defaultMenuOrder: number;
  defaultLaunchTargetKey?: string;
  listing: "listed" | "unlisted";
  status: "active" | "deprecated";
  launchTargets: ApplicationLaunchTarget[];
  latestVersion: ApplicationVersion;
  installed: boolean;
  installationId?: string;
  installationStatus?: ApplicationStatus;
  createdAt: string;
  updatedAt: string;
}

// ---------- tenant developer applications (ADR-0017) ----------

export type DeveloperApplicationStatus = "active" | "disabled";

export interface DeveloperApplication {
  id: string;
  tenantId: string;
  slug: string;
  name: string;
  description?: string;
  status: DeveloperApplicationStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDeveloperApplicationPayload {
  slug: string;
  name: string;
  description?: string;
}

export interface UpdateDeveloperApplicationPayload {
  name?: string;
  description?: string;
}

export type DeveloperApplicationRouteKind = "app" | "runtime";
export type DeveloperApplicationRouteTargetType = "upstream" | "static_site";
export type DeveloperApplicationRouteVisibility =
  "public" | "authenticated" | "internal";

export interface DeveloperApplicationRoute {
  id: string;
  tenantId: string;
  developerAppId: string;
  kind: DeveloperApplicationRouteKind;
  hostname: string;
  targetType: DeveloperApplicationRouteTargetType;
  upstreamUrl?: string;
  visibility: DeveloperApplicationRouteVisibility;
  status: DeveloperApplicationStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertDeveloperApplicationRoutePayload {
  targetType?: DeveloperApplicationRouteTargetType;
  upstreamUrl?: string;
  visibility: DeveloperApplicationRouteVisibility;
}

export interface StaticSite {
  id: string;
  tenantId: string;
  ownerKind: "faas" | "developer_app";
  developerAppId?: string;
  name: string;
  status: "active" | "disabled";
  spaFallback: boolean;
  activeReleaseId?: string;
  generation: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface StaticSiteRelease {
  id: string;
  tenantId: string;
  siteId: string;
  version: string;
  archiveDigest: string;
  status: "ready" | "retired";
  fileCount: number;
  totalBytes: number;
  createdBy: string;
  createdAt: string;
  activatedAt?: string;
}

export interface StaticSiteUploadSession {
  upload: {
    id: string;
    tenantId: string;
    siteId: string;
    version: string;
    archiveDigest: string;
    archiveSize: number;
    contentType: string;
    status: "uploading" | "validating" | "ready" | "failed";
    expiresAt: string;
    createdAt: string;
    updatedAt: string;
  };
  uploadUrl: string;
  headers: Record<string, string>;
}

export interface StaticSiteReleasePage {
  items: StaticSiteRelease[];
  total: number;
}

export type ManagedAppConfigKind =
  "string" | "integer" | "boolean" | "json" | "secret";

export interface ManagedAppConfigEntry {
  configured: boolean;
  key: string;
  kind: ManagedAppConfigKind;
  value?: unknown;
}

export interface ManagedAppConfigEnvironment {
  entryCount: number;
  generation: number;
  name: string;
  updatedAt: string;
}

export interface ManagedAppConfigSnapshot {
  applicationId: string;
  digest: string;
  entries: ManagedAppConfigEntry[];
  environment: string;
  generation: number;
  createdBy?: string;
  reason?: string;
  updatedAt?: string;
}

export interface SaveManagedAppConfigEntry {
  key: string;
  kind: ManagedAppConfigKind;
  value?: unknown;
}

export interface SaveManagedAppConfigPayload {
  entries: SaveManagedAppConfigEntry[];
  expectedGeneration: number;
  reason?: string;
}

export type OAuthGrantType =
  "authorization_code" | "refresh_token" | "client_credentials";
export type OAuthTokenEndpointAuthMethod = "client_secret_basic" | "none";

export type OAuthClientStatus = "active" | "disabled";

export interface OAuthClient {
  id: string;
  developerAppId: string;
  clientId: string;
  name: string;
  allowedGrantTypes: OAuthGrantType[];
  tokenEndpointAuthMethod: OAuthTokenEndpointAuthMethod;
  scopeCodes: string[];
  status: OAuthClientStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOAuthClientPayload {
  name: string;
  allowedGrantTypes: OAuthGrantType[];
  tokenEndpointAuthMethod?: OAuthTokenEndpointAuthMethod;
  clientId?: string;
  scopeCodes: string[];
}

export interface UpdateOAuthClientPayload {
  name?: string;
  allowedGrantTypes?: OAuthGrantType[];
  scopeCodes?: string[];
}

export type OAuthClientSecretStatus = "active" | "retiring" | "revoked";

// Secret metadata never contains the plaintext client secret.
export interface OAuthClientSecret {
  id: string;
  oauthClientId: string;
  version: number;
  status: OAuthClientSecretStatus;
  expiresAt?: string;
  createdAt: string;
  revokedAt?: string;
  lastUsedAt?: string;
}

export interface CreateOAuthClientSecretPayload {
  expiresAt?: string;
}

// clientSecret is returned exactly once and must not be added to metadata DTOs.
export interface CreateOAuthClientSecretResponse extends OAuthClientSecret {
  clientSecret: string;
}

export interface RevokeOAuthClientSecretResponse {
  revoked: boolean;
}

export interface OAuthRedirectUri {
  id: string;
  oauthClientId: string;
  redirectUri: string;
  createdAt: string;
}

export interface CreateOAuthRedirectUriPayload {
  redirectUri: string;
}

export interface DeleteOAuthRedirectUriResponse {
  deleted: boolean;
}

export type OAuthScopeRiskLevel = "low" | "medium" | "high" | "critical";
export type OAuthScopeGrantPolicy = "self_service" | "platform_approval";

export interface OAuthScope {
  code: string;
  name: string;
  description?: string;
  riskLevel: OAuthScopeRiskLevel;
  grantPolicy: OAuthScopeGrantPolicy;
  userConsentRequired: boolean;
}

export interface DeveloperApplicationScopeGrant {
  developerAppId: string;
  scopeCode: string;
  grantedBy: string;
  grantedAt: string;
  revokedAt?: string;
}

export interface UpdateDeveloperApplicationScopesPayload {
  scopeCodes: string[];
}

export type ScopeApprovalRequestStatus =
  "pending" | "approved" | "rejected" | "cancelled";

export interface ScopeApprovalRequest {
  id: string;
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  developerAppId: string;
  developerAppSlug: string;
  developerAppName: string;
  scopeCode: string;
  scopeName: string;
  scopeDescription: string;
  riskLevel: OAuthScopeRiskLevel;
  userConsentRequired: boolean;
  requestedBy: string;
  requestReason: string;
  status: ScopeApprovalRequestStatus;
  reviewedBy?: string;
  cancelledBy?: string;
  grantId?: string;
  resolvedAt?: string;
  decisionReason?: string;
  createdAt: string;
}

export interface SubmitScopeApprovalRequestsPayload {
  scopeCodes: string[];
  reason: string;
}

export interface OAuthAuthorizationDetails {
  client_id: string;
  client_name: string;
  app_id: string;
  app_slug: string;
  app_name: string;
  tenant_id: string;
  user_id: string;
  redirect_uri: string;
  scopes: string[];
  scope_details: Array<{
    code: string;
    name: string;
    description?: string;
    risk_level: OAuthScopeRiskLevel;
    user_consent_required: boolean;
  }>;
  state?: string;
  code_challenge_method: "S256";
  decision_endpoint: string;
}

export interface OAuthAuthorizationDecisionPayload {
  responseType: "code";
  clientId: string;
  redirectUri: string;
  scope: string;
  state?: string;
  codeChallenge: string;
  codeChallengeMethod: "S256";
  decision: "approve" | "deny";
}

export interface OAuthAuthorizationDecisionResponse {
  redirect_uri: string;
}

export interface OAuthUserAuthorization {
  clientId: string;
  clientName: string;
  appId: string;
  appSlug: string;
  appName: string;
  appStatus: DeveloperApplicationStatus;
  clientStatus: OAuthClientStatus;
  scopes: Array<
    Pick<OAuthScope, "code" | "name" | "description" | "riskLevel">
  >;
  lastGrantedAt: string;
}
